import React, { FC, useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import moment from 'moment';
import { toast } from 'react-toastify';

import { ITimesheetAttachmentDTO, IAmountTimeCategoryWithAttachmentDTO } from 'src/api/dtos';
import { IError } from 'src/api/types';
import { useTimesheetData, useTimesheetEdit, useTimesheetSelection } from 'src/features/timesheets/providers';
import { AddIcon, DocumentUploadModal, Drawer } from 'src/shared/components';
import { DAY_DATE_MONTH_FORMAT } from 'src/shared/constants';
import { PermissionsEnums } from 'src/shared/enums';
import { ComponentWithPermissions } from 'src/shared/hocs';
import { useTranslate } from 'src/shared/providers';
import { getErrorMessage } from 'src/shared/utils';

import { TimesheetExpense } from './TimesheetExpense';

const MAX_FILES = 1;

interface Props {
  open: boolean;
  onClose: () => void;
}

export const TimesheetExpenseDrawer: FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslate();

  const { timeCategories, loadingTimeCategories } = useTimesheetData();
  const { selected } = useTimesheetSelection();
  const { editingTimesheet, onExpensesChange, onDeleteFile } = useTimesheetEdit();

  const [expenses, setExpenses] = useState<IAmountTimeCategoryWithAttachmentDTO[]>();
  const [initialExpenses, setInitialExpenses] = useState<IAmountTimeCategoryWithAttachmentDTO[]>();

  const [openDocumentUploadModal, setOpenDocumentUploadModal] = useState<number | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);

  useEffect(() => {
    const defaultTimeCategory = timeCategories[0];

    if (!defaultTimeCategory) {
      return;
    }

    const defaultExpense: IAmountTimeCategoryWithAttachmentDTO = {
      ...defaultTimeCategory,
      hours: 0,
      memoAmount: 0,
      note: null,
      dollarAmount: null,
      attachment: {
        referenceId: null,
        contentType: null,
        fileName: null,
        fileSizeInBytes: null,
        file: null,
        canDownload: true,
      },
    };

    const row = editingTimesheet?.rows.find((row) => row.id === selected?.id);
    const cell = row?.cells.find((cell) => cell.date === selected?.field);
    const expenses = cell?.expenses.length ? cell.expenses : [defaultExpense];

    setExpenses(expenses);
    setInitialExpenses(expenses);
  }, [editingTimesheet, selected, timeCategories]);

  const handleDelete = (expenseIndex: number) => {
    setExpenses(expenses?.filter((_, index) => index !== expenseIndex));
  };

  const handleTimeCategoryChange = (expenseIndex: number, timeCategoryId: number) => {
    setExpenses((expenses) => {
      if (!expenses) {
        return;
      }

      const clonedExpenses = cloneDeep(expenses);

      const timeCategory = timeCategories.find((timeCategory) => timeCategory.id === timeCategoryId);

      if (!timeCategory) {
        return;
      }

      return clonedExpenses.map((expense, index) =>
        index === expenseIndex ? { ...expense, ...timeCategory } : expense,
      );
    });
  };

  const handleMemoAmountChange = (expenseIndex: number, memoAmount: number) => {
    setExpenses((expenses) => {
      if (!expenses) {
        return;
      }

      const clonedExpenses = cloneDeep(expenses);

      return clonedExpenses.map((expense, index) => (index === expenseIndex ? { ...expense, memoAmount } : expense));
    });
  };

  const handleNoteChange = (expenseIndex: number, note: string) => {
    setExpenses((expenses) => {
      if (!expenses) {
        return;
      }
      const clonedExpenses = cloneDeep(expenses);
      return clonedExpenses.map((expense, index) => (index === expenseIndex ? { ...expense, note } : expense));
    });
  };

  const handleAttachmentChange = (expenseIndex: number, file: File | null, referenceId: string | null) => {
    setExpenses((expenses) => {
      if (!expenses) {
        return;
      }
      const clonedExpenses = cloneDeep(expenses);
      return clonedExpenses.map((expense, index) =>
        index === expenseIndex
          ? {
              ...expense,
              attachment: {
                referenceId: referenceId,
                contentType: file?.type || null,
                fileName: file?.name || null,
                fileSizeInBytes: file?.size || null,
                canDownload: true,
                file,
              },
            }
          : expense,
      );
    });
  };

  const handleAdd = () => {
    const defaultTimeCategory = timeCategories[0];

    if (!defaultTimeCategory) {
      return;
    }

    setExpenses((expenses) => {
      if (!expenses) {
        return;
      }

      const clonedExpenses = cloneDeep(expenses);

      return [
        ...clonedExpenses,
        {
          ...defaultTimeCategory,
          hours: 0,
          memoAmount: 0,
          note: null,
          dollarAmount: null,
          attachment: {
            referenceId: null,
            contentType: null,
            fileName: null,
            fileSizeInBytes: null,
            canDownload: true,
            file: null,
          },
        },
      ];
    });
  };

  const handleSave = () => {
    if (!selected || !expenses) {
      return;
    }

    onExpensesChange(
      selected.id,
      selected.field,
      expenses.filter((expense) => !!expense.memoAmount),
    );
    onClose();
  };

  const handleUpload = (file: File) => {
    try {
      setUploading(true);

      handleAttachmentChange(openDocumentUploadModal!, file, new Date().getTime().toString());
    } catch (error) {
      handleAttachmentChange(openDocumentUploadModal!, null, null);
      const errorMessage = getErrorMessage(error as IError, t('global.oopsAnErrorOccurred'));
      toast.error(errorMessage);
    } finally {
      setUploading(false);
      setOpenDocumentUploadModal(null);
    }
  };

  const handleDeleteFile = (attachment?: ITimesheetAttachmentDTO | null) => {
    if (!attachment?.referenceId) {
      return;
    }

    const expenseIndex = expenses?.findIndex((expense) => expense.attachment?.referenceId === attachment?.referenceId);
    if (expenseIndex !== undefined && expenseIndex !== -1) {
      handleAttachmentChange(expenseIndex, null, null);
    }

    onDeleteFile(attachment.referenceId);
  };

  const currentFile = useMemo<File | null>(() => {
    if (openDocumentUploadModal !== null && expenses) {
      return expenses[openDocumentUploadModal].attachment?.file ?? null;
    }
    return null;
  }, [openDocumentUploadModal, expenses]);

  if (!expenses || !selected) {
    return null;
  }

  const isDisabled = expenses.some((expense) => !expense.description || !expense.memoAmount);
  const isDirty = !isEqual(initialExpenses, expenses);

  return (
    <Drawer
      title={t('timesheets.addAnExpense')}
      description={moment(selected.field).format(DAY_DATE_MONTH_FORMAT)}
      overflow={false}
      open={open}
      onClose={onClose}
    >
      <Stack gap={2} height="100%">
        <Stack gap={2} overflow="auto" maxHeight="100%">
          {loadingTimeCategories && (
            <Skeleton className="rounded" variant="rectangular" animation="wave" height={200} width="100%" />
          )}
          {expenses.map((expense, index) => (
            <Stack key={`expense-${index}`} direction="column" gap={2}>
              <TimesheetExpense
                title={expenses.length <= 1 ? null : `${t('timesheets.expense')} ${index + 1}`}
                timeCategoryId={expense.id}
                note={expense.note ?? ''}
                memoAmount={expense.memoAmount}
                attachment={expense?.attachment}
                onDelete={() => handleDelete(index)}
                onNoteChange={(note) => handleNoteChange(index, note)}
                onMemoAmountChange={(memoAmount) => handleMemoAmountChange(index, memoAmount)}
                onTimeCategoryChange={(timeCategoryId) => handleTimeCategoryChange(index, timeCategoryId)}
                onDeleteAttachment={() => {
                  handleDeleteFile(expense.attachment);
                }}
              />
              <ComponentWithPermissions permissions={PermissionsEnums.WTS_EXPENSE_ATTACHMENTS}>
                <Button
                  color="secondary"
                  disabled={!!expense?.attachment?.contentType}
                  startIcon={<AddIcon size={16} />}
                  onClick={() => setOpenDocumentUploadModal(index)}
                  sx={{ width: 'fit-content', fontSize: 12 }}
                >
                  {t('files.addAttachment')}
                </Button>
              </ComponentWithPermissions>
            </Stack>
          ))}
        </Stack>
        <Stack direction="row" gap={1} paddingBottom={2}>
          <Button color="secondary" onClick={onClose}>
            {t('global.cancel')}
          </Button>
          <Button
            color="secondary"
            startIcon={<AddIcon />}
            disabled={loadingTimeCategories || isDisabled}
            onClick={handleAdd}
          >
            {t('timesheets.addMoreExpenses')}
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={!selected || !expenses || !isDirty || isDisabled}>
            {t('global.ok')}
          </Button>
        </Stack>
      </Stack>
      <DocumentUploadModal
        maxFiles={MAX_FILES}
        open={!isNil(openDocumentUploadModal)}
        onClose={() => setOpenDocumentUploadModal(null)}
        loading={uploading}
        files={currentFile ? [currentFile] : []}
        setFiles={(files) => {
          handleUpload(files[0] ?? null);
        }}
        onDeleteFile={() => {
          handleAttachmentChange(openDocumentUploadModal!, null, null);
        }}
      />
    </Drawer>
  );
};
